const STORAGE_KEY = 'wc:wishlist:v1';

function safeParseJson(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
}

function decodeHtmlEntities(value) {
  if (typeof value !== 'string') return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function stripHtml(value) {
  if (typeof value !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = value;
  return div.textContent || '';
}

function sanitizeText(value) {
  return stripHtml(decodeHtmlEntities(value)).trim();
}

function normalizeItem(item) {
  if (!item || typeof item !== 'object') return null;
  const handle = typeof item.handle === 'string' ? item.handle.trim() : '';
  const url = typeof item.url === 'string' ? item.url.trim() : '';
  if (!handle && !url) return null;

  return {
    handle,
    url,
    title: typeof item.title === 'string' ? item.title : '',
    image: typeof item.image === 'string' ? item.image : '',
    price: sanitizeText(typeof item.price === 'string' ? item.price : ''),
  };
}

function getWishlist() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  const parsed = safeParseJson(raw);
  if (!Array.isArray(parsed)) return [];

  const normalized = parsed.map(normalizeItem).filter(Boolean);
  return dedupeByHandleOrUrl(normalized);
}

function dedupeByHandleOrUrl(items) {
  const seen = new Set();
  const result = [];
  for (const item of items) {
    const key = item.handle ? `h:${item.handle}` : `u:${item.url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }
  return result;
}

function setWishlist(items) {
  const normalized = dedupeByHandleOrUrl(items.map(normalizeItem).filter(Boolean));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

function findItemIndex(items, { handle, url }) {
  if (handle) return items.findIndex((x) => x.handle === handle);
  if (url) return items.findIndex((x) => x.url === url);
  return -1;
}

function isInWishlist(handleOrUrl) {
  const items = getWishlist();
  return items.some((x) => x.handle === handleOrUrl || x.url === handleOrUrl);
}

function setButtonState(button, isActive) {
  if (!button) return;
  button.classList.toggle('is-active', Boolean(isActive));
  button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

  const labelNode = button.querySelector('[data-wishlist-label]');
  const labelOn = button.getAttribute('data-label-on') || '';
  const labelOff = button.getAttribute('data-label-off') || '';
  if (labelNode) labelNode.textContent = isActive ? labelOn : labelOff;
}

function syncAllButtons() {
  const items = getWishlist();
  const handles = new Set(items.map((x) => x.handle).filter(Boolean));
  const urls = new Set(items.map((x) => x.url).filter(Boolean));

  for (const button of document.querySelectorAll('[data-wishlist-toggle]')) {
    const handle = button.getAttribute('data-product-handle') || '';
    const url = button.getAttribute('data-product-url') || '';
    setButtonState(button, (handle && handles.has(handle)) || (url && urls.has(url)));
  }
}

function updateHeaderCount() {
  const count = getWishlist().length;
  for (const node of document.querySelectorAll('[data-wishlist-count]')) {
    node.textContent = String(count);
  }
  for (const bubble of document.querySelectorAll('[data-wishlist-bubble]')) {
    bubble.toggleAttribute('hidden', count === 0);
  }
}

function formatMoneyCents(cents) {
  const shopify = window.Shopify;
  if (shopify && typeof shopify.formatMoney === 'function') {
    try {
      const format = shopify.money_format || '${{amount}}';
      return shopify.formatMoney(cents, format);
    } catch {
      return '';
    }
  }
  if (typeof cents !== 'number') return '';
  return (cents / 100).toFixed(2);
}

async function fetchProductByHandle(handle) {
  if (!handle) return null;
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const product = await res.json();
    return product;
  } catch {
    return null;
  }
}

async function enrichItem(item) {
  if (item.title && item.image && item.price) return item;
  if (!item.handle) return item;

  const product = await fetchProductByHandle(item.handle);
  if (!product) return item;

  const image = Array.isArray(product.images) && product.images.length ? product.images[0] : '';
  const price = typeof product.price === 'number' ? formatMoneyCents(product.price) : '';
  const url = item.url || (typeof product.url === 'string' ? product.url : `/products/${product.handle}`);

  return normalizeItem({
    ...item,
    title: item.title || product.title,
    image: item.image || image,
    price: item.price || price,
    url,
  });
}

function renderWishlistModal(dialog) {
  if (!dialog) return;

  const itemsContainer = dialog.querySelector('[data-wishlist-items]');
  const template = dialog.querySelector('[data-wishlist-item-template]');
  const hasItemsState = dialog.querySelector('[data-wishlist-has-items]');
  const emptyState = dialog.querySelector('[data-wishlist-empty-state]');
  const clearButton = dialog.querySelector('[data-wishlist-clearbutton], [data-wishlist-clear]');
  const countNode = dialog.querySelector('[data-wishlist-modal-count]');

  const removeLabel = dialog.getAttribute('data-remove-label') || '';
  const countSingular = dialog.getAttribute('data-count-singular') || '';
  const countPlural = dialog.getAttribute('data-count-plural') || '';
  const items = getWishlist();

  if (hasItemsState) hasItemsState.toggleAttribute('hidden', items.length === 0);
  if (emptyState) emptyState.toggleAttribute('hidden', items.length !== 0);
  if (clearButton) clearButton.toggleAttribute('hidden', items.length === 0);

  if (countNode) {
    if (items.length === 0) {
      countNode.textContent = '';
    } else {
      const label = items.length === 1 ? countSingular : countPlural;
      const labelText = label ? ` ${label}` : '';
      countNode.textContent = `(${items.length}${labelText})`;
    }
  }

  if (!itemsContainer || !template) return;
  itemsContainer.innerHTML = '';

  const jobs = items.map((x) => enrichItem(x));
  Promise.all(jobs).then((enriched) => {
    const finalItems = setWishlist(enriched);
    updateHeaderCount();
    syncAllButtons();

    for (const item of finalItems) {
      const node = template.content.firstElementChild.cloneNode(true);

      const link = node.querySelector('[data-wishlist-item-link]');
      const image = node.querySelector('[data-wishlist-item-image]');
      const title = node.querySelector('[data-wishlist-item-title]');
      const price = node.querySelector('[data-wishlist-item-price]');
      const remove = node.querySelector('[data-wishlist-remove]');

      if (link && item.url) link.setAttribute('href', item.url);
      if (image && item.image) image.setAttribute('src', item.image);
      if (image) image.setAttribute('alt', item.title || '');
      if (title) title.textContent = item.title || '';
      if (price) price.textContent = item.price || '';
      if (remove) {
        remove.setAttribute('aria-label', removeLabel);
        remove.setAttribute('data-product-handle', item.handle || '');
        remove.setAttribute('data-product-url', item.url || '');
      }

      itemsContainer.appendChild(node);
    }
  });
}

function openDialog(dialog) {
  if (!dialog) return;
  renderWishlistModal(dialog);
  if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
  dialog.setAttribute('open', '');
}

function closeDialog(dialog) {
  if (!dialog) return;
  if (typeof dialog.close === 'function' && dialog.open) dialog.close();
  dialog.removeAttribute('open');
}

function toggleWishlistFromButton(button) {
  const payloadJson = button.getAttribute('data-wishlist-payload');
  const payload = payloadJson ? safeParseJson(payloadJson) : null;

  const handle = button.getAttribute('data-product-handle') || payload?.handle || '';
  const url = button.getAttribute('data-product-url') || payload?.url || '';

  if (!handle && !url) return;

  const items = getWishlist();
  const index = findItemIndex(items, { handle, url });

  if (index >= 0) {
    items.splice(index, 1);
  } else {
    const newItem = normalizeItem({
      handle,
      url,
      title: payload?.title,
      image: payload?.image,
      price: payload?.price,
    });
    if (newItem) items.push(newItem);
  }

  setWishlist(items);
  updateHeaderCount();
  syncAllButtons();
}

function initWishlist() {
  updateHeaderCount();
  syncAllButtons();

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleWishlistFromButton(toggle);
      return;
    }

    const openButton = event.target.closest('[data-wishlist-open]');
    if (openButton) {
      event.preventDefault();
      const dialogId = openButton.getAttribute('data-wishlist-open') || '';
      const dialog = dialogId ? document.getElementById(dialogId) : document.querySelector('[data-wishlist-dialog]');
      openDialog(dialog);
      return;
    }

    const closeButton = event.target.closest('[data-wishlist-close]');
    if (closeButton) {
      event.preventDefault();
      const dialog = closeButton.closest('dialog');
      closeDialog(dialog);
      return;
    }

    const clearButton = event.target.closest('[data-wishlist-clearbutton], [data-wishlist-clear]');
    if (clearButton) {
      event.preventDefault();
      setWishlist([]);
      updateHeaderCount();
      syncAllButtons();
      const dialog = clearButton.closest('dialog');
      renderWishlistModal(dialog);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      const row = removeButton.closest('.wishlist-item');
      const dialog = removeButton.closest('dialog');
      if (row && !row.classList.contains('is-removing')) {
        row.style.height = `${row.offsetHeight}px`;
        row.offsetHeight;
        row.classList.add('is-removing');
        window.setTimeout(() => {
          const handle = removeButton.getAttribute('data-product-handle') || '';
          const url = removeButton.getAttribute('data-product-url') || '';

          const items = getWishlist();
          const index = findItemIndex(items, { handle, url });
          if (index >= 0) {
            items.splice(index, 1);
            setWishlist(items);
          }

          updateHeaderCount();
          syncAllButtons();
          renderWishlistModal(dialog);
        }, 190);
      } else {
        const handle = removeButton.getAttribute('data-product-handle') || '';
        const url = removeButton.getAttribute('data-product-url') || '';

        const items = getWishlist();
        const index = findItemIndex(items, { handle, url });
        if (index >= 0) {
          items.splice(index, 1);
          setWishlist(items);
        }

        updateHeaderCount();
        syncAllButtons();
        renderWishlistModal(dialog);
      }
      return;
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    updateHeaderCount();
    syncAllButtons();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist, { once: true });
} else {
  initWishlist();
}
